import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Descriptions, Form, Row, Spin } from "antd";
import TableSearch from "js/components/TableSearch";
import LandlordList from "js/components/Landlords/LandlordListSelect/LandlordList";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { useLandlords } from "hooks/useLandlords";
import {
  LandlordListSelectProps,
  LandlordOption,
} from "js/components/Landlords/types";
import { PaginationTable } from "js/components/types";

/**
 * Referencia estable para el caso "sin resultados". NO MUTAR: se comparte
 * entre renders.
 */
const EMPTY_LANDLORDS: LandlordOption[] = [];

const LandlordListSelect: React.FC<LandlordListSelectProps> = ({
  selectedLandlord,
  setSelectedLandlord,
}) => {
  const screen = useBreakpoint();
  const listRef = useRef(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [filteredLandlord, setFilteredLandlord] = React.useState<
    LandlordOption[]
  >([]);
  const [landlordsData, setLandlordsData] = React.useState<LandlordOption[]>(
    [],
  );
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // Fetch landlords using TanStack Query
  // Automatic refetch on cache invalidation (from create/update/delete mutations)
  const { data: landlordsResponse, isLoading } = useLandlords({
    current: pagination.current,
    pageSize: pagination.pageSize,
  });

  const classToAvoidTexttruncate = {
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  // Transform landlords data from hook response
  useEffect(() => {
    if (landlordsResponse) {
      // Idempotente: `pagination` viaja dentro del queryKey, asi que escribir
      // `total` sin necesidad cambia la clave y dispara un refetch extra.
      setPagination((p) =>
        p.total === landlordsResponse.length
          ? p
          : { ...p, total: landlordsResponse.length },
      );
      const sortedLandlords = landlordsResponse
        .sort((a, b) => (a.legal_type ?? 0) - (b.legal_type ?? 0))
        .map((landlord) => ({
          label: landlord.legal_entity_name
            ? landlord.legal_entity_name
            : `${landlord.name} ${landlord.first_surname} ${landlord.second_surname}`.trim(),
          value: `${landlord?.id}`,
          fields: landlord,
        }));
      setLandlordsData(sortedLandlords);
    }
  }, [landlordsResponse]);

  /**
   * Referencia estable y escritura idempotente.
   *
   * Antes esto era un arrow inline en el JSX cuyo cuerpo hacia
   * `setFilteredLandlord([])`: un array NUEVO en cada llamada, asi que el
   * estado cambiaba SIEMPRE. Combinado con el useEffect de TableSearch (que
   * dependia de la identidad de `onClear`) producia un bucle infinito
   * INCONDICIONAL en cada montaje del selector, sin depender de que la query
   * trajera datos.
   */
  const handleClearSearch = useCallback(() => {
    setIsSearching(false);
    setFilteredLandlord((prev) =>
      prev.length === 0 ? prev : EMPTY_LANDLORDS,
    );
  }, []);

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (!searchValue) {
        handleClearSearch();
        return;
      }

      setIsSearching(true);
      const lowercasedValue = searchValue.toLowerCase();
      const filtered = landlordsData.filter((property) => {
        const { label, fields } = property;
        return (
          label?.toLowerCase().includes(lowercasedValue) ||
          fields?.legal_entity_name?.toLowerCase().includes(lowercasedValue)
        );
      });

      setFilteredLandlord(filtered);
    },
    [landlordsData, handleClearSearch],
  );

  const scrollToTop = () => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  return (
    <Row gutter={{ xs: 24, sm: 24, md: 24, lg: 24 }} style={{ width: "100%" }}>
      <Col
        xs={24}
        sm={24}
        md={12}
        lg={12}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Row
          align={"middle"}
          style={{
            gap: 10,
            marginBottom: Object.keys(selectedLandlord).length > 0 ? 10 : 0,
          }}
        >
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={Object.keys(selectedLandlord).length > 0 ? 16 : 24}
          >
            <TableSearch
              reapplyOn={landlordsData}
              style={{
                width: "100%",
              }}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              label="Propietario"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={6}>
            {Object.keys(selectedLandlord).length > 0 && (
              <Form.Item
                style={{
                  margin: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={isLoading}
                  onClick={() => {
                    setSelectedLandlord({});
                    scrollToTop();
                  }}
                >
                  Limpiar selección
                </Button>
              </Form.Item>
            )}
          </Col>
        </Row>
        {isLoading ? (
          <Spin />
        ) : (
          <div>
            <LandlordList
              propertiesOptions={landlordsData}
              selectedLandlord={selectedLandlord}
              onSelect={setSelectedLandlord}
              isSearching={isSearching}
              filteredLandlord={filteredLandlord}
              setFilteredLandlord={setFilteredLandlord}
              listRef={listRef}
              scrollToTop={scrollToTop}
            />
          </div>
        )}
      </Col>
      <Col xs={24} sm={24} md={12} lg={12}>
        <Card
          style={{
            width: "100%",
            height: "100%",
            marginTop: !screen.lg ? 15 : "0",
          }}
        >
          <Descriptions
            title={
              Object.keys(selectedLandlord).length === 0 ? (
                <p style={classToAvoidTexttruncate}>
                  Seleccione un propietario para ver su información aquí
                </p>
              ) : (
                <p style={classToAvoidTexttruncate}>
                  Datos del propietario seleccionado
                </p>
              )
            }
            size="middle"
            layout="horizontal"
            column={1}
          >
            <Descriptions.Item label="Nombre">
              {selectedLandlord.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Apellido paterno">
              {selectedLandlord.first_surname || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Apellido materno">
              {selectedLandlord.second_surname || "-"}
            </Descriptions.Item>
            {selectedLandlord.legal_entity_name && (
              <Descriptions.Item label="Razón social">
                {selectedLandlord.legal_entity_name || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Correo">
              {selectedLandlord.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Teléfono">
              {selectedLandlord.phone_number || "-"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
    </Row>
  );
};

export default LandlordListSelect;
